import { createDateWithTimeZoneOffset } from '@dispatch/shared';
import {
  useGetQualifiedResources,
  useGetTask,
  usePermissions,
  useRescheduleTask,
  useTabHandler,
  useSetTaskStatus,
  useSetTaskLockStatus,
  useGetTaskEvents,
  useGetResourcesPaginated,
  useCurrentUserResource,
  useGetGeoStatus,
  useForceReassign,
  useCompany,
} from '@dispatch/shared/hooks';
import {
  ReassignFormBody,
  TaskRescheduleRequest,
} from '@dispatch/shared/types';
import {
  useNotification,
  useNotificationMutationFactory,
  useResourceDataGrid,
} from '@dispatch/web/ui';
import { useParams } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useValidateTaskErrors } from '../../tasks/utils/ErrorHandlers';
import { ComplexLossDetails } from '@dispatch/web/features/complex-loss';

export const ComplexLossDetailsPage = () => {
  const { company, perils, rescheduleReasons } = useCompany();
  const { isAdjuster } = useCurrentUserResource();
  const { permissions } = usePermissions();
  const queryClient = useQueryClient();
  const params = useParams();
  const { notifySuccess, notifyError } = useNotification();
  const { handleError } = useValidateTaskErrors(notifyError);
  const taskId: string = params['taskId'] || '';
  const { task, taskStatus } = useGetTask(taskId);
  const resourceMeta = useResourceDataGrid();
  const [disableButtons, setDisableButtons] = useState<boolean>();
  const { status: geoStatus } = useGetGeoStatus(
    task?.$geoTracking?.events || []
  );

  const resources = useGetResourcesPaginated({
    filters: { ...resourceMeta?.resourcesFilters, isActive: true },
    enabled: !resourceMeta?.showOnlyQualified,
  });

  const qualifiedResources = useGetQualifiedResources(
    task?.id,
    resourceMeta?.showOnlyQualified
  );
  const { taskEvents, taskEventsStatus } = useGetTaskEvents(taskId);

  const onRescheduleError = (errorMessage: {
    status: number;
    message: string;
  }) => {
    handleError(errorMessage);
  };

  const notifyStartTaskSuccess = () => {
    notifySuccess('Success', 'Task has been successfully started');
  };

  const notifyStartTaskFailed = () => {
    notifyError('Error', 'Task was not started');
  };

  const notifyCompleteTaskSuccess = () => {
    notifySuccess('Success', 'Task has been successfully completed');
  };

  const notifyCompleteTaskFailed = () => {
    notifyError('Error', 'Task was not completed');
  };

  const onSettled = () => {
    setDisableButtons(false);
  };

  const onForceReassign = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess(
        'Task Reassigned Successfully',
        'Task has been successfully reassigned'
      );
      queryClient.invalidateQueries(['resources']);
      queryClient.invalidateQueries(['schedule-data']);
      queryClient.invalidateQueries(['summaries']);
      queryClient.invalidateQueries(['task']);
      queryClient.invalidateQueries(['resource-tasks-assigned']);
      queryClient.invalidateQueries(['resource-tasks-unassigned']);
    } else {
      notifyError('Error', 'Tasks were not reassigned');
    }
  };

  const notifications = useNotificationMutationFactory('Task', 'update');
  const rescheduleMutation = useRescheduleTask(() => {
    notifications.notifySuccess();
    refresh();
  }, onRescheduleError);
  const { mutate: reassignTask } = useForceReassign(onForceReassign);
  const { currentTab, setTab } = useTabHandler();
  const startTaskMutation = useSetTaskStatus(
    notifyStartTaskSuccess,
    notifyStartTaskFailed,
    onSettled
  );
  const completeTaskMutation = useSetTaskStatus(
    notifyCompleteTaskSuccess,
    notifyCompleteTaskFailed,
    onSettled
  );

  const handleSave = (form: ReassignFormBody) => {
    const request: TaskRescheduleRequest = {
      resourceId: form.resourceId,
      reasonCode: form.reasonCode,
      appointmentStart: createDateWithTimeZoneOffset(
        form?.assignmentHandling?.appointmentStart,
        form?.claim?.timeZone
      ).toJSON(),
      appointmentEnd: createDateWithTimeZoneOffset(
        form?.assignmentHandling?.appointmentEnd,
        form?.claim?.timeZone
      ).toJSON(),
      isPrecise: false,
    };

    rescheduleMutation.mutate({
      taskId: task?.id || '',
      request,
    });
  };

  const handleForceReassign = (resoruceId: string, reasonCode: string) => {
    reassignTask({
      taskIds: [task?.id || ''],
      resourceId: resoruceId,
      reasonCode: reasonCode,
    });
  };

  const statuses = () => {
    if (taskStatus === 'loading') {
      return 'loading';
    }

    return 'success';
  };

  const getTaskEventsStatus = () => {
    if (taskEventsStatus === 'loading') {
      return 'loading';
    }

    return 'success';
  };

  const notifyCancelTaskSuccess = () => {
    notifySuccess('', 'Task has been successfully cancelled');
  };

  const notifyCancelTaskFailed = () => {
    notifyError('Error', 'Task was not cancelled');
  };

  const setCancelStatusMutation = useSetTaskStatus(
    notifyCancelTaskSuccess,
    notifyCancelTaskFailed
  );
  const handleCancelTask = useCallback(
    (value) => {
      setCancelStatusMutation.mutate({
        taskId: task?.id || '',
        status: 'cancelled',
        request: { reason: value },
      });
    },
    [setCancelStatusMutation, task?.id]
  );

  const {
    setLockedStatusMutation,
    setLockStatusInCache,
    invalidateLockStatus,
  } = useSetTaskLockStatus();

  const handleLockTask = async () => {
    const lockedAt = task?.$msg.lockedAt;
    const statusLabel = lockedAt ? 'unlocked' : 'locked';
    try {
      setLockStatusInCache(task?.id);
      await setLockedStatusMutation.mutateAsync({
        taskId: task?.id || '',
        status: statusLabel,
      });
      notifySuccess('', `Task has been successfully ${statusLabel}`);
      invalidateLockStatus(task?.id);
    } catch {
      notifyError('Error', `Task was not ${statusLabel}`);
      invalidateLockStatus(task?.id);
    }
  };

  const handleStartTask = useCallback(() => {
    setDisableButtons(true);

    startTaskMutation.mutate({
      taskId: task?.id || '',
      status: 'pending',
    });
  }, [startTaskMutation, task?.id]);

  const handleCompleteTask = useCallback(() => {
    setDisableButtons(true);
    completeTaskMutation.mutate({
      taskId: task?.id || '',
      status: 'completed',
    });
  }, [completeTaskMutation, task?.id]);

  const resourceResolver = React.useMemo(() => {
    if (resourceMeta?.showOnlyQualified) {
      // this needs to be casted to this type until the api has a more consistent structure.
      // mui data grid isnt allowing multiple rowId mappings, so this is a fix for that.
      return qualifiedResources;
    } else {
      return resources;
    }
  }, [resourceMeta?.showOnlyQualified, resources, qualifiedResources]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries(['resources']);
  }, [queryClient]);

  return (
    <ComplexLossDetails
      company={company}
      data={task}
      showOnlyQualified={{
        value: resourceMeta?.showOnlyQualified,
        setShowOnlyQualified: resourceMeta?.setShowOnlyQualified,
      }}
      resources={resourceResolver || []}
      onSave={handleSave}
      onForceReassign={handleForceReassign}
      onCancelTask={handleCancelTask}
      onLockTask={handleLockTask}
      onStartTask={handleStartTask}
      onCompleteTask={handleCompleteTask}
      status={statuses()}
      permissions={permissions}
      currentTab={currentTab}
      setTab={setTab}
      perils={perils}
      taskEvents={taskEvents}
      getTaskEventsStatus={getTaskEventsStatus()}
      rescheduleReasons={rescheduleReasons}
      notifyError={notifyError}
      resourceMeta={resourceMeta}
      disableButtons={disableButtons}
      isAdjuster={isAdjuster}
      geoStatus={geoStatus}
      cancelStatus={setCancelStatusMutation.status}
    />
  );
};
